import { Fragment, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { translate } from 'src/translate';
import { useLanguage } from 'src/LanguageContext';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function DropDown({
  updateStaff,
  staffList,
  title,
  choosenStaffId,
  needsTranslation = false,
  disabled = false,
  skipDefaulting = false,
}) {
  const customizer = useSelector((state) => state.customizer);

  const { language } = useLanguage();

  const [selected, setSelected] = useState(() => {
    if (!staffList || staffList.length === 0) return null;
    if (choosenStaffId) {
      const found = staffList.filter((e) => e.id === choosenStaffId)[0];
      return found || staffList[0];
    }
    return staffList[0];
  });

  // Sync parent state when choosenStaffId changes OR when staffList becomes available
  useEffect(() => {
    if (!staffList || staffList.length === 0) return;

    if (choosenStaffId) {
      const found = staffList.find((e) => e.id === choosenStaffId);
      if (found) {
        setSelected((current) => (current?.id === found.id ? current : found));
      }
    } else if (!skipDefaulting) {
      const firstStaff = staffList[0];
      if (firstStaff) {
        setSelected((current) => (current?.id === firstStaff.id ? current : firstStaff));
        updateStaff(firstStaff.id);
      }
    }
  }, [choosenStaffId, staffList, skipDefaulting]);

  return (
    <Listbox
      as="div"
      disabled={disabled}
      value={selected}
      onChange={(value) => {
        updateStaff(value.id);
        setSelected(value);
      }}
    >
      {({ open }) => (
        <>
          <Listbox.Label className="block text-sm font-medium leading-6 text-gray-900 mt-2">
            {title}
          </Listbox.Label>
          <div className="relative mt-2">
            <Listbox.Button
              className={`relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-watsPrimary sm:text-sm sm:leading-6 ${
                customizer.activeDir === 'rtl' ? 'text-right' : 'text-left'
              }`}
            >
              <span className="block truncate">
                {needsTranslation ? translate(selected?.name, language) : selected?.name}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {staffList?.map((person) => (
                  <Listbox.Option
                    key={person.id}
                    className={({ active }) =>
                      classNames(
                        active ? ' bg-watsPrimary text-white' : 'text-gray-900',
                        'relative cursor-default select-none py-2 pl-3 pr-9',
                      )
                    }
                    value={person}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={classNames(
                            selected ? 'font-semibold' : 'font-normal',
                            'block truncate',
                          )}
                        >
                          {needsTranslation ? translate(person?.name, language) : person.name}
                        </span>

                        {selected ? (
                          <span
                            className={classNames(
                              active ? 'text-white' : ' text-watsPrimary',
                              'absolute inset-y-0 right-0 flex items-center pr-4',
                            )}
                          >
                            <CheckIcon className="h-5 w-5 text-watsPrimary" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  );
}
